import { z } from 'zod'

import {
  requireDataClassName,
  resolveDataClass,
  serializeDataItems,
  unknownEntityError,
} from '../shared/entity-serializer'
import { resolveEntityReference } from '../shared/resolveEntityReference'
import type { AdapterReferenceFilter } from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import { getEntityRegistry } from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

/**
 * Single adapter search attempt. Every call — primary attempt and every token-drop
 * fallback retry — routes through here so `referenceFilter` is never silently dropped.
 * Dropping it on a fallback would widen the search population (e.g. all Persons instead
 * of internal users only), reintroducing exactly the scoping bug CRM-785 closed.
 */
async function runSearchAttempt(
  dcName: string,
  terms: string,
  referenceFilter: AdapterReferenceFilter | undefined,
): Promise<unknown[]> {
  return getHostRuntime().data.query(dcName, {
    search: terms,
    limit: 10,
    ...(referenceFilter ? { referenceFilter } : {}),
  })
}

export const searchEntitiesTool = defineTool({
  name: 'search_entities',
  description:
    'Full-text search across CRM records by name or keyword. ' +
    'Omit dataClass to search across all entity types at once (use this when the user says "find X" without specifying a type). ' +
    'Provide dataClass to scope to a specific type — e.g. "find company arlag", "search for person smith". ' +
    "When resolving a REFERENCE field to its _id, pass both dataClass = that field's targetClass and referenceFilter = that field's referenceFilter (both from get_write_schema) — this scopes the search to the correct records (e.g. internal users only for an owner / responsible field). " +
    'Returns up to 10 matching records with their key fields.',
  inputSchema: () =>
    z.object({
      dataClass: z
        .string()
        .optional()
        .describe(
          'Entity type to search, e.g. Company, Person, Ticket, Opportunity, Order, Quote, Activity, Lead. Omit to search across all entity types.',
        ),
      query: z
        .string()
        .describe('The search term — name, partial name, or keyword'),
      referenceFilter: z
        .object({
          isExternal: z.boolean().optional(),
          isPerson: z.boolean().optional(),
        })
        .optional()
        .describe(
          "When resolving a reference field, pass the field's referenceFilter from get_write_schema to scope the search to the correct sub-population (e.g. { isExternal: false } for internal users).",
        ),
    }),

  async run(args, ctx) {
    const { dataClass, query, referenceFilter } = args

    if (!dataClass) {
      const results = await resolveEntityReference(null, query, ctx)
      return results.map((r) => ({
        _id: r.id,
        _url: r.url,
        entityType: r.entityType,
        displayTitle: r.title,
        confidence: r.confidence,
      }))
    }

    const dcName = requireDataClassName(dataClass)
    if (!dcName) {
      return unknownEntityError(dataClass)
    }
    if (!resolveDataClass(dataClass)) {
      return unknownEntityError(dataClass)
    }

    // Contact is the polymorphic reference base (a customer / responsibleAgent
    // resolves to a Company OR an external Person). It is deliberately NOT a
    // registry entity — adding it there would duplicate every Company/Person hit
    // in the global fan-out — but it IS a valid reference target, so let it
    // through the searchable gate here. The adapter's referenceFilter option
    // carries the full filter (e.g. isExternal) for Contact, matching the manual picker.
    if (
      !getHostRuntime().data.isPolymorphicReferenceBase(dcName) &&
      !getEntityRegistry()[dcName]?.searchable
    ) {
      return []
    }

    // Primary attempt with the full query.
    let items = await runSearchAttempt(dcName, query, referenceFilter)

    // Token-drop fallback: when a multi-token query returns nothing, retry each
    // token individually and union the results (dedupe by id, cap at 10).
    // This rescues one-char typos in either word — e.g. "Narayana Chikkamm" still
    // finds "Narayana Chikkam" because the "Narayana" token alone matches.
    // referenceFilter is threaded into every runSearchAttempt call so reference-
    // scoped lookups (e.g. internal users only) stay scoped on every retry.
    const tokens = query.trim().split(/\s+/)
    if (items.length === 0 && tokens.length > 1) {
      const seenIds = new Set<string>()
      const fallbackItems: unknown[] = []
      for (const token of tokens) {
        const tokenResults = await runSearchAttempt(
          dcName,
          token,
          referenceFilter,
        )
        for (const item of tokenResults) {
          // warning: adapter returns unknown[]; items are DataItem-like with .id()
          const id = (item as { id(): string }).id()
          if (!seenIds.has(id)) {
            seenIds.add(id)
            fallbackItems.push(item)
            if (fallbackItems.length >= 10) {
              break
            }
          }
        }
        if (fallbackItems.length >= 10) {
          break
        }
      }
      items = fallbackItems
    }

    return serializeDataItems(items, dataClass, ctx)
  },
})
